import { useCallback, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client/react'
import {
  GET_EMAIL_CAMPAIGNS,
  GET_EMAIL_CAMPAIGN,
  GET_GMAIL_QUOTA,
  CREATE_EMAIL_CAMPAIGN,
  UPDATE_EMAIL_CAMPAIGN,
  DELETE_EMAIL_CAMPAIGN,
  REVIEW_BULK_EMAIL,
  CHECK_CAMPAIGN_REPLIES,
  MARK_CAMPAIGN_PROVIDER_FOLLOWUP,
} from '@/lib/operations/emailCampaigns'
import { useLangGraphStream } from '@/hooks/useLangGraphStream'
import { useCampaignAssistStore } from '@/state/campaignAssistStore'
import type { EmailCampaign, EmailReviewResult, GmailQuotaInfo } from '@/lib/types'

// ─── Query hooks ──────────────────────────────────────────────────────────────

export function useEmailCampaigns(clientId: string) {
  const { data, loading, error, refetch } = useQuery<{ emailCampaigns: EmailCampaign[] }>(
    GET_EMAIL_CAMPAIGNS,
    { variables: { clientId }, skip: !clientId },
  )
  return { campaigns: data?.emailCampaigns ?? [], loading, error, refetch }
}

export function useEmailCampaign(clientId: string, campaignId: string) {
  const { data, loading, error, refetch } = useQuery<{ emailCampaign: EmailCampaign | null }>(
    GET_EMAIL_CAMPAIGN,
    { variables: { clientId, campaignId }, skip: !clientId || !campaignId },
  )
  return { campaign: data?.emailCampaign ?? null, loading, error, refetch }
}

export function useGmailQuota(clientId: string) {
  const { data, loading, error, refetch } = useQuery<{ gmailQuota: GmailQuotaInfo }>(
    GET_GMAIL_QUOTA,
    { variables: { clientId }, skip: !clientId },
  )
  return { quota: data?.gmailQuota ?? null, loading, error, refetch }
}

// ─── Mutation hooks ───────────────────────────────────────────────────────────

interface CreateCampaignProviderInput {
  providerId: string
  providerNpi: string
  providerName: string
  email: string
}

interface CreateCampaignInput {
  name: string
  subject: string
  body: string
  providers: CreateCampaignProviderInput[]
}

export function useCreateEmailCampaign() {
  const [mutate, { loading }] = useMutation<{ createEmailCampaign: EmailCampaign }>(
    CREATE_EMAIL_CAMPAIGN,
  )
  const createCampaign = (clientId: string, input: CreateCampaignInput) =>
    mutate({
      variables: { clientId, input },
      refetchQueries: [{ query: GET_EMAIL_CAMPAIGNS, variables: { clientId } }],
    })
  return { createCampaign, loading }
}

export function useDeleteEmailCampaign() {
  const [mutate, { loading }] = useMutation<{ deleteEmailCampaign: boolean }>(
    DELETE_EMAIL_CAMPAIGN,
  )
  const deleteCampaign = (clientId: string, campaignId: string) =>
    mutate({
      variables: { clientId, campaignId },
      refetchQueries: [{ query: GET_EMAIL_CAMPAIGNS, variables: { clientId } }],
    })
  return { deleteCampaign, loading }
}

export function useUpdateEmailCampaign() {
  const [mutate, { loading }] = useMutation<{ updateEmailCampaign: { id: string; name: string; subject: string; body: string; status: string; updatedAt: string } }>(
    UPDATE_EMAIL_CAMPAIGN,
  )
  const updateCampaign = (
    clientId: string,
    campaignId: string,
    patch: { name?: string; subject?: string; body?: string },
  ) => mutate({ variables: { clientId, campaignId, patch } })
  return { updateCampaign, loading }
}

export function useGenerateCampaignEmail(clientId: string) {
  const { stream, isStreaming, reset: resetStream } = useLangGraphStream({
    onEvent: (type, data) => {
      const store = useCampaignAssistStore.getState()
      if (type === 'node_update') {
        try {
          const parsed = JSON.parse(data) as {
            node?: string
            data?: { subject?: string; body?: string }
          }
          if (
            parsed.node === 'generate' &&
            parsed.data?.subject &&
            parsed.data?.body
          ) {
            store.setImproved(parsed.data.subject, parsed.data.body, '')
          }
        } catch { /* ignore malformed */ }
        return
      }
      if (type === 'error') {
        try {
          const parsed = JSON.parse(data) as { message?: string }
          store.setError(parsed.message ?? data)
        } catch {
          store.setError(data)
        }
      }
    },
    onDone: () => {
      const store = useCampaignAssistStore.getState()
      if (store.status === 'improving') store.setStatus('done')
    },
    onError: (message) => {
      useCampaignAssistStore.getState().setError(message)
    },
  })

  const generateEmail = useCallback(
    async (params: { targetAudience: string }) => {
      useCampaignAssistStore.getState().reset()
      useCampaignAssistStore.getState().setStatus('improving')
      await stream(
        `${import.meta.env.VITE_API_BASE_URL}/api/clients/${clientId}/campaigns/improve-stream`,
        { mode: 'generate', target_audience: params.targetAudience },
      )
    },
    [stream, clientId],
  )

  const reset = useCallback(() => {
    resetStream()
    useCampaignAssistStore.getState().reset()
  }, [resetStream])

  return { generateEmail, isGenerating: isStreaming, reset }
}

export function useReviewEmailCampaign() {
  const [mutate, { loading }] = useMutation<{ reviewBulkEmail: EmailReviewResult }>(
    REVIEW_BULK_EMAIL,
  )
  const reviewCampaign = (clientId: string, campaignId: string) =>
    mutate({ variables: { clientId, campaignId } })
  return { reviewCampaign, loading }
}

export function useCheckCampaignReplies() {
  const [mutate, { loading }] = useMutation<{ checkCampaignReplies: EmailCampaign }>(
    CHECK_CAMPAIGN_REPLIES,
  )
  const checkReplies = (clientId: string, campaignId: string) =>
    mutate({
      variables: { clientId, campaignId },
      refetchQueries: [{ query: GET_EMAIL_CAMPAIGN, variables: { clientId, campaignId } }],
    })
  return { checkReplies, loading }
}

export function useMarkCampaignProviderFollowup() {
  const [mutate, { loading }] = useMutation<{ markCampaignProviderFollowup: EmailCampaign }>(
    MARK_CAMPAIGN_PROVIDER_FOLLOWUP,
  )
  const markFollowup = (
    clientId: string,
    campaignId: string,
    providerNpi: string,
    needsFollowup: boolean,
  ) =>
    mutate({
      variables: { clientId, campaignId, providerNpi, needsFollowup },
      refetchQueries: [{ query: GET_EMAIL_CAMPAIGN, variables: { clientId, campaignId } }],
    })
  return { markFollowup, loading }
}

// ─── SSE: AI improve email ────────────────────────────────────────────────────

export function useImproveCampaignEmail(clientId: string) {
  const { stream, isStreaming, reset: resetStream } = useLangGraphStream({
    onEvent: (type, data) => {
      const store = useCampaignAssistStore.getState()
      if (type === 'node_update') {
        try {
          const parsed = JSON.parse(data) as {
            node?: string
            data?: { improved_subject?: string; improved_body?: string; changes_summary?: string }
          }
          if (
            parsed.node === 'improve' &&
            parsed.data?.improved_subject &&
            parsed.data?.improved_body
          ) {
            store.setImproved(
              parsed.data.improved_subject,
              parsed.data.improved_body,
              parsed.data.changes_summary ?? '',
            )
          }
        } catch { /* ignore malformed */ }
        return
      }
      if (type === 'error') {
        try {
          const parsed = JSON.parse(data) as { message?: string }
          store.setError(parsed.message ?? data)
        } catch {
          store.setError(data)
        }
      }
    },
    onDone: () => {
      const store = useCampaignAssistStore.getState()
      if (store.status === 'improving') store.setStatus('done')
    },
    onError: (message) => {
      useCampaignAssistStore.getState().setError(message)
    },
  })

  const improve = useCallback(
    async (subject: string, body: string) => {
      useCampaignAssistStore.getState().reset()
      useCampaignAssistStore.getState().setStatus('improving')
      await stream(
        `${import.meta.env.VITE_API_BASE_URL}/api/clients/${clientId}/campaigns/improve-stream`,
        { subject, body },
      )
    },
    [stream, clientId],
  )

  const reset = useCallback(() => {
    resetStream()
    useCampaignAssistStore.getState().reset()
  }, [resetStream])

  return { improve, isImproving: isStreaming, reset }
}

// ─── SSE: bulk send campaign ──────────────────────────────────────────────────

type SendStatus = 'idle' | 'sending' | 'done' | 'error'

export interface ProviderSendProgress {
  sent: number
  failed: number
  total: number
}

export function useSendCampaign(clientId: string, campaignId: string) {
  const [status, setStatus] = useState<SendStatus>('idle')
  const [progress, setProgress] = useState<ProviderSendProgress>({ sent: 0, failed: 0, total: 0 })
  const [providerStatuses, setProviderStatuses] = useState<Record<string, 'pending' | 'sent' | 'failed'>>({})
  const [quotaWarning, setQuotaWarning] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const { stream, isStreaming, reset: resetStream } = useLangGraphStream({
    onEvent: (type, data) => {
      if (type === 'email_sent') {
        try {
          const parsed = JSON.parse(data) as { providerNpi: string }
          setProviderStatuses((prev) => ({ ...prev, [parsed.providerNpi]: 'sent' }))
          setProgress((prev) => ({ ...prev, sent: prev.sent + 1 }))
        } catch { /* ignore */ }
        return
      }
      if (type === 'email_failed') {
        try {
          const parsed = JSON.parse(data) as { providerNpi: string }
          setProviderStatuses((prev) => ({ ...prev, [parsed.providerNpi]: 'failed' }))
          setProgress((prev) => ({ ...prev, failed: prev.failed + 1 }))
        } catch { /* ignore */ }
        return
      }
      if (type === 'quota_warning') {
        try {
          const parsed = JSON.parse(data) as { message: string }
          setQuotaWarning(parsed.message)
        } catch { /* ignore */ }
        return
      }
      if (type === 'done') {
        setStatus('done')
        return
      }
      if (type === 'error') {
        try {
          const parsed = JSON.parse(data) as { message?: string }
          setErrorMessage(parsed.message ?? data)
        } catch {
          setErrorMessage(data)
        }
        setStatus('error')
      }
    },
    onDone: () => {
      setStatus((prev) => (prev === 'sending' ? 'done' : prev))
    },
    onError: (message) => {
      setErrorMessage(message)
      setStatus('error')
    },
  })

  const send = useCallback(
    async (totalProviders: number) => {
      setStatus('sending')
      setProgress({ sent: 0, failed: 0, total: totalProviders })
      setProviderStatuses({})
      setQuotaWarning(null)
      setErrorMessage(null)
      await stream(
        `${import.meta.env.VITE_API_BASE_URL}/api/clients/${clientId}/campaigns/${campaignId}/send-stream`,
        {},
      )
    },
    [stream, clientId, campaignId],
  )

  const reset = useCallback(() => {
    resetStream()
    setStatus('idle')
    setProgress({ sent: 0, failed: 0, total: 0 })
    setProviderStatuses({})
    setQuotaWarning(null)
    setErrorMessage(null)
  }, [resetStream])

  return { send, status, isStreaming, progress, providerStatuses, quotaWarning, errorMessage, reset }
}
